import React from 'react';
import api from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../../components/ui/dialog';
import { Separator } from '../../../components/ui/separator';
import { toast } from 'sonner';
import { Plus, Printer, Trash2, X, AlertTriangle, Package, ArrowRightLeft, History } from 'lucide-react';

const T = { success: '#22c55e', danger: '#ef4444', info: '#3b82f6' };

export const PositionDetailDialog = ({
  open, onOpenChange, selectedNode, positionStock, positionHistory, bins,
  onAddArticle, onTransferItem, onLoadDetail, onFetchData, onSetMinStock
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md bg-zinc-900 border-zinc-700 text-white">
      <DialogHeader>
        <DialogTitle className="font-mono text-lg">{selectedNode?.code}</DialogTitle>
        <DialogDescription className="text-zinc-400">{selectedNode?.cabinetName} &rarr; {selectedNode?.shelfName} &rarr; {selectedNode?.positionName}</DialogDescription>
      </DialogHeader>
      <div className="space-y-4">
        <div className="grid grid-cols-3 gap-2">
          <div className="p-3 rounded-lg bg-zinc-800 border border-zinc-700/50 hover:border-indigo-500/30 transition-colors"><p className="text-[10px] text-zinc-500">Dulap</p><p className="font-mono font-bold text-sm">{selectedNode?.cabinetName}</p></div>
          <div className="p-3 rounded-lg bg-zinc-800 border border-zinc-700/50 hover:border-green-500/30 transition-colors"><p className="text-[10px] text-zinc-500">Raft</p><p className="font-mono font-bold text-sm">{selectedNode?.shelfName}</p></div>
          <div className="p-3 rounded-lg bg-zinc-800 border border-zinc-700/50 hover:border-orange-500/30 transition-colors"><p className="text-[10px] text-zinc-500">Pozitie</p><p className="font-mono font-bold text-sm">{selectedNode?.positionName}</p></div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button className="flex items-center gap-2 p-2.5 rounded-lg bg-zinc-800 hover:bg-emerald-500/10 border border-zinc-700 hover:border-emerald-500/30 transition-all text-xs" onClick={onAddArticle} data-testid="action-add-article">
            <Plus className="w-4 h-4 text-emerald-400" /><span className="text-zinc-300">Adauga Articol</span>
          </button>
          <button className="flex items-center gap-2 p-2.5 rounded-lg bg-zinc-800 hover:bg-blue-500/10 border border-zinc-700 hover:border-blue-500/30 transition-all text-xs" onClick={async () => {
            try {
              const res = await api.post(`/bins/qr-export?format=industrial&fields=code,cabinet,shelf,position`, [selectedNode?.positionId], { responseType: 'blob' });
              const url = window.URL.createObjectURL(new Blob([res.data]));
              const a = document.createElement('a'); a.href = url; a.download = `qr_${selectedNode?.code}.pdf`; a.click();
              toast.success('Eticheta QR generata!');
            } catch { toast.error('Eroare'); }
          }} data-testid="action-print-qr">
            <Printer className="w-4 h-4 text-blue-400" /><span className="text-zinc-300">Print Eticheta QR</span>
          </button>
          <button className="flex items-center gap-2 p-2.5 rounded-lg bg-zinc-800 hover:bg-orange-500/10 border border-zinc-700 hover:border-orange-500/30 transition-all text-xs" onClick={async () => {
            if (positionStock.length === 0) { toast.error('Pozitia e deja goala'); return; }
            if (!window.confirm(`Goliti pozitia ${selectedNode?.code}? Se va scoate tot stocul.`)) return;
            try {
              for (const item of positionStock) {
                await api.post('/stock/movement', { article_id: item.article_id, from_bin_id: selectedNode?.positionId, quantity: item.quantity, movement_type: 'out', created_by: 'admin' });
              }
              toast.success('Pozitie golita!'); onLoadDetail(selectedNode?.positionId); onFetchData();
            } catch (e) { toast.error(e.response?.data?.detail || 'Eroare'); }
          }} data-testid="action-empty-position">
            <Trash2 className="w-4 h-4 text-orange-400" /><span className="text-zinc-300">Goleste Pozitia</span>
          </button>
          <button className="flex items-center gap-2 p-2.5 rounded-lg bg-zinc-800 hover:bg-red-500/10 border border-zinc-700 hover:border-red-500/30 transition-all text-xs" onClick={async () => {
            if (positionStock.length > 0) { toast.error('Nu se poate sterge - are stoc!'); return; }
            if (!window.confirm(`Stergeti definitiv pozitia ${selectedNode?.code}?`)) return;
            try {
              await api.delete(`/bins/${selectedNode?.positionId}`);
              toast.success('Pozitie stearsa!'); onOpenChange(false); onFetchData();
            } catch (e) { toast.error(e.response?.data?.detail || 'Eroare'); }
          }} data-testid="action-delete-position">
            <X className="w-4 h-4 text-red-400" /><span className="text-zinc-300">Sterge Pozitia</span>
          </button>
          <button className="flex items-center gap-2 p-2.5 rounded-lg bg-zinc-800 hover:bg-yellow-500/10 border border-zinc-700 hover:border-yellow-500/30 transition-all text-xs col-span-2" onClick={() => {
            const bin = bins.find(b => b.id === selectedNode?.positionId);
            onSetMinStock(bin?.min_stock || 0);
          }} data-testid="action-set-min-stock">
            <AlertTriangle className="w-4 h-4 text-yellow-400" /><span className="text-zinc-300">Seteaza Stoc Minim</span>
          </button>
        </div>

        <Separator className="bg-zinc-700" />

        <div>
          <h4 className="text-xs font-bold text-zinc-400 mb-2 flex items-center gap-1.5"><Package className="w-3.5 h-3.5" /> Articole Stocate</h4>
          {positionStock.length === 0 ? (
            <p className="text-center text-zinc-600 py-6 text-xs">Pozitie goala</p>
          ) : positionStock.map((item, i) => (
            <div key={i} className="flex items-center justify-between p-2.5 rounded-lg bg-zinc-800 border border-zinc-700/50 mb-1.5 group hover:border-emerald-500/20 transition-colors">
              <div>
                <p className="text-sm font-medium">{item.article_name || item.article_code}</p>
                <p className="text-[10px] text-zinc-500">Cod: {item.article_code || 'N/A'}</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-lg font-mono font-bold text-emerald-400">{item.quantity}</p>
                  <p className="text-[10px] text-zinc-500">{item.unit || 'buc'}</p>
                </div>
                <button className="opacity-0 group-hover:opacity-100 transition-opacity p-1 rounded hover:bg-zinc-700" title="Muta articol" onClick={() => onTransferItem(item)}><ArrowRightLeft className="w-3.5 h-3.5 text-blue-400" /></button>
              </div>
            </div>
          ))}
        </div>

        {positionHistory.length > 0 && (
          <>
            <Separator className="bg-zinc-700" />
            <div>
              <h4 className="text-xs font-bold text-zinc-400 mb-2 flex items-center gap-1.5"><History className="w-3.5 h-3.5" /> Istoric Miscari</h4>
              {positionHistory.slice(0, 8).map((m, i) => (
                <div key={i} className="flex items-center gap-2 p-2 text-[11px] text-zinc-400 border-l-2 ml-2 pl-3 mb-1" style={{ borderColor: m.movement_type === 'in' ? T.success : m.movement_type === 'out' ? T.danger : T.info }}>
                  <span className={`font-mono font-bold ${m.movement_type === 'in' ? 'text-emerald-400' : m.movement_type === 'out' ? 'text-red-400' : 'text-blue-400'}`}>{m.movement_type?.toUpperCase()}</span>
                  <span className="flex-1">{m.article_name || 'Articol'}</span>
                  <span className="font-mono font-bold">{m.quantity}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </DialogContent>
  </Dialog>
);
